/**
 * Mapa route → parent para navegação «voltar» hierárquica (D11 / NAV-017).
 * Top-level sections → /dashboard; fluxos → rota pai documentada.
 *
 * Consumido por nativeBackHandler, BackButton e cold-start bootstrap (futuro).
 */

/** Seções de primeiro nível na bottom nav / sidebar — back sempre vai ao início. */
export const TOP_LEVEL_SECTIONS = [
  '/patients',
  '/calendar',
  '/plantoes',
  '/documentos',
  '/chat-ia',
  '/settings',
  '/inventory',
  '/calculator',
  '/last-results',
  '/documentation',
  '/feedback',
  '/notifications',
  '/noticias',
] as const

const TOP_LEVEL_SET = new Set<string>(TOP_LEVEL_SECTIONS)

/** Rotas raiz — sem alvo hierárquico (delegar a back-to-exit / histórico do browser). */
const ROOT_ROUTES = new Set(['/', '/dashboard'])

/** Mapeamento estático route → parent (fluxos e filhos de settings/cadastros). */
export const ROUTE_PARENT_MAP: Record<string, string> = {
  ...Object.fromEntries(TOP_LEVEL_SECTIONS.map((route) => [route, '/dashboard'])),

  '/add-exam': '/dashboard',
  '/add-encounter': '/dashboard',

  '/inventory/new': '/inventory',

  '/plantoes/registros': '/plantoes',

  '/documentos/historico': '/documentos',
  '/documentos/modelos': '/documentos',

  '/settings/profile': '/settings',
  '/doctor/settings/certificate': '/settings',
  '/doctor/settings/local-ai': '/settings',
  '/settings/google-calendar': '/settings',
  '/settings/google-calendar/callback': '/settings/google-calendar',
  '/settings/feed': '/settings',
  '/settings/open-source': '/settings',
  '/settings/admin-users': '/settings',
  '/settings/ai-providers': '/settings',
  '/settings/admin-platform/prompts': '/settings',
  '/settings/cadastros': '/settings',
  '/settings/cadastros/exam-types': '/settings/cadastros',
  '/settings/cadastros/exam-types/edit': '/settings/cadastros/exam-types',
  '/settings/cadastros/locations': '/settings/cadastros',
  '/settings/cadastros/locations/edit': '/settings/cadastros/locations',
  '/settings/cadastros/attendance-types': '/settings/cadastros',
  '/settings/cadastros/attendance-types/edit': '/settings/cadastros/attendance-types',
  '/settings/cadastros/clinical-conducts': '/settings/cadastros',
  '/settings/cadastros/active-ingredients': '/settings/cadastros',
  '/settings/cadastros/active-ingredients/edit': '/settings/cadastros/active-ingredients',
  '/settings/cadastros/cid-codes': '/settings/cadastros',
  '/settings/cadastros/cid-codes/edit': '/settings/cadastros/cid-codes',
  '/settings/cadastros/procedure-codes': '/settings/cadastros',
  '/settings/cadastros/procedure-codes/edit': '/settings/cadastros/procedure-codes',
  '/settings/import': '/settings',
  '/settings/import/cid': '/settings/import',
  '/settings/import/procedure-codes': '/settings/import',
  '/settings/import/patients': '/settings/import',
  '/settings/import/exams': '/settings/import',
  '/settings/import/document-models': '/settings/import',
  '/settings/import/ai-prompts': '/settings/import',
  '/auth/vidaas/callback': '/doctor/settings/certificate',
  '/auth/iti/callback': '/doctor/settings/certificate',
}

type RouteParentPattern = {
  test: RegExp
  parent: (pathname: string) => string
}

const ROUTE_PARENT_PATTERNS: RouteParentPattern[] = [
  {
    test: /^\/patients\/[^/]+$/,
    parent: () => '/patients',
  },
  {
    test: /^\/inventory\/[^/]+\/edit$/,
    parent: () => '/inventory',
  },
  {
    test: /^\/plantoes\/registros\/[^/]+$/,
    parent: () => '/plantoes/registros',
  },
  {
    test: /^\/documentos\/editar\/[^/]+$/,
    parent: () => '/documentos/historico',
  },
  {
    test: /^\/settings\/cadastros\/active-ingredients\/view\/[^/]+$/,
    parent: () => '/settings/cadastros/active-ingredients',
  },
]

export const normalizeRoutePath = (pathname: string): string => {
  const trimmed = pathname.replace(/\/$/, '')
  return trimmed.length > 0 ? trimmed : '/'
}

const stripLastSegment = (pathname: string): string => {
  const parts = pathname.split('/').filter(Boolean)
  parts.pop()
  return parts.length > 0 ? `/${parts.join('/')}` : '/'
}

/**
 * Resolve o alvo hierárquico do botão voltar para `pathname`.
 * @returns rota pai ou `null` na raiz (/dashboard) — sinal para back-to-exit.
 */
export const resolveBackTarget = (pathname: string): string | null => {
  const path = normalizeRoutePath(pathname)

  if (ROOT_ROUTES.has(path)) {
    return null
  }

  const exact = ROUTE_PARENT_MAP[path]
  if (exact) {
    return exact
  }

  for (const rule of ROUTE_PARENT_PATTERNS) {
    if (rule.test.test(path)) {
      return rule.parent(path)
    }
  }

  const parent = stripLastSegment(path)
  if (ROOT_ROUTES.has(parent)) {
    return null
  }
  if (TOP_LEVEL_SET.has(parent)) {
    return '/dashboard'
  }
  return parent
}
